import React, { useState, useEffect } from 'react';
import axios, { API } from '@/utils/api';
import { toast } from 'sonner';
import { Shield, Lock, Unlock, Eye, AlertTriangle, CheckCircle, Clock, Users, Activity } from 'lucide-react';

const SecurityManager = () => {
  const [overview, setOverview] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [lockouts, setLockouts] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    try {
      setLoading(true);
      const [overviewRes, logsRes, lockoutsRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/admin/security/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/security/audit-log?limit=20`, { withCredentials: true }),
        axios.get(`${API}/admin/security/lockouts`, { withCredentials: true }),
        axios.get(`${API}/admin/security/active-sessions?limit=20`, { withCredentials: true })
      ]);
      
      setOverview(overviewRes.data);
      setAuditLogs(logsRes.data.logs || []);
      setLockouts(lockoutsRes.data.lockouts || []);
      setActiveSessions(sessionsRes.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch security data:', error);
      toast.error('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockAccount = async (email) => {
    if (!window.confirm(`Are you sure you want to unlock the account for ${email}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/security/lockouts/${encodeURIComponent(email)}`, {
        withCredentials: true
      });
      toast.success(`Account ${email} has been unlocked`);
      fetchSecurityData();
    } catch (error) {
      toast.error('Failed to unlock account');
    }
  };

  const handleRevokeUserSessions = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to revoke all sessions for ${userEmail}? They will be logged out immediately.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/security/sessions/${userId}`, {
        withCredentials: true
      });
      toast.success(`All sessions revoked for ${userEmail}`);
      fetchSecurityData();
    } catch (error) {
      toast.error('Failed to revoke sessions');
    }
  };

  const getEventTypeColor = (eventType) => {
    switch (eventType) {
      case 'login_success':
        return 'text-green-400';
      case 'account_locked':
      case 'login_blocked_lockout':
        return 'text-red-400';
      case 'admin_unlock_account':
      case 'admin_revoke_all_sessions':
        return 'text-yellow-400';
      default:
        return 'text-gray-400';
    }
  };

  const formatEventType = (eventType) => {
    return eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-8 h-8 text-pink-500" />
        <div>
          <h2 className="text-2xl font-bold text-white">Security Management</h2>
          <p className="text-gray-400 text-sm">Monitor and manage application security</p>
        </div>
      </div>

      {/* Stats Cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-4 border border-green-500/30">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-xs text-green-400 uppercase">Logins (24h)</span>
            </div>
            <div className="text-2xl font-bold text-white">{overview.successful_logins_24h}</div>
          </div>

          <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-xl p-4 border border-red-500/30">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-xs text-red-400 uppercase">Failed (24h)</span>
            </div>
            <div className="text-2xl font-bold text-white">{overview.failed_logins_24h}</div>
          </div>

          <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-xl p-4 border border-orange-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-5 h-5 text-orange-400" />
              <span className="text-xs text-orange-400 uppercase">Locked Accounts</span>
            </div>
            <div className="text-2xl font-bold text-white">{overview.active_lockouts}</div>
          </div>

          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-4 border border-blue-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-400" />
              <span className="text-xs text-blue-400 uppercase">Active Sessions</span>
            </div>
            <div className="text-2xl font-bold text-white">{overview.active_sessions}</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-4 border border-purple-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-purple-400" />
              <span className="text-xs text-purple-400 uppercase">Events (7d)</span>
            </div>
            <div className="text-2xl font-bold text-white">{overview.security_events_7d}</div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 rounded-xl p-4 border border-yellow-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              <span className="text-xs text-yellow-400 uppercase">Rate Limits</span>
            </div>
            <div className="text-2xl font-bold text-white">{overview.rate_limits_hit_24h}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        {['overview', 'lockouts', 'sessions', 'audit'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === tab
                ? 'bg-pink-500/20 text-pink-400 border-b-2 border-pink-500'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-[#1a0527]/50 rounded-xl border border-pink-500/20 p-6">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white mb-4">Security Status</h3>
            <div className="grid gap-4">
              <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-white">Security Headers</span>
                </div>
                <span className="text-green-400 font-semibold">Active</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-white">Rate Limiting</span>
                </div>
                <span className="text-green-400 font-semibold">Active</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-white">Account Lockout Protection</span>
                </div>
                <span className="text-green-400 font-semibold">Active</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-white">httpOnly Cookie Auth</span>
                </div>
                <span className="text-green-400 font-semibold">Active</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-white">CSRF Protection</span>
                </div>
                <span className="text-green-400 font-semibold">Active</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-white">Password Strength Enforcement</span>
                </div>
                <span className="text-green-400 font-semibold">Active</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'lockouts' && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Locked Accounts</h3>
            {lockouts.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Lock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No accounts are currently locked</p>
              </div>
            ) : (
              <div className="space-y-3">
                {lockouts.map((lockout) => (
                  <div key={lockout.email} className="flex items-center justify-between p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                    <div>
                      <div className="text-white font-medium">{lockout.email}</div>
                      <div className="text-sm text-gray-400">
                        {lockout.attempts} failed attempts • {lockout.minutes_remaining} min remaining
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnlockAccount(lockout.email)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                    >
                      <Unlock className="w-4 h-4" />
                      Unlock
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'sessions' && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Active Sessions</h3>
            {activeSessions.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No active sessions</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {activeSessions.map((session) => (
                  <div key={session.session_id || session.user_id} className="flex items-center justify-between p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <div>
                      <div className="text-white font-medium">{session.user_email || session.user_id}</div>
                      <div className="text-sm text-gray-400">
                        {session.device?.family || session.device_type || 'Unknown'} • {typeof session.browser === 'object' ? session.browser.family : session.browser} • {typeof session.os === 'object' ? session.os.family : session.os}
                      </div>
                      <div className="text-xs text-gray-500">
                        IP: {session.ip_address} • Created: {new Date(session.created_at).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevokeUserSessions(session.user_id, session.user_email || session.user_id)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                    >
                      <Lock className="w-4 h-4" />
                      Revoke All
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Security Audit Log</h3>
            {auditLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No security events recorded yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {auditLogs.map((log) => (
                  <div key={`${log.timestamp}-${log.identifier}-${log.event_type}`} className="flex items-start gap-4 p-3 bg-gray-800/50 rounded-lg">
                    <div className={`mt-1 ${getEventTypeColor(log.event_type)}`}>
                      {log.event_type.includes('success') ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : log.event_type.includes('locked') || log.event_type.includes('blocked') ? (
                        <AlertTriangle className="w-5 h-5" />
                      ) : (
                        <Activity className="w-5 h-5" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${getEventTypeColor(log.event_type)}`}>
                          {formatEventType(log.event_type)}
                        </span>
                        <span className="text-gray-500 text-sm">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400">{log.identifier}</div>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          {JSON.stringify(log.details)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={fetchSecurityData}
          className="px-4 py-2 bg-pink-500/20 text-pink-400 rounded-lg hover:bg-pink-500/30 transition-colors"
        >
          Refresh Data
        </button>
      </div>
    </div>
  );
};

export default SecurityManager;
